import type { WsUrlContext } from "api/ws/websocketManager";
import { isJwtExpired } from "utils/jwt";
import { httpStatusOf } from "utils/httpStatusOf";

/**
 * After this many consecutive handshakes that never opened, ask the server whether the session is still
 * valid even when the token LOOKS fresh (local clock behind the server's, or a token revoked early).
 * Three keeps a brief network blip from costing a REST call while bounding a dead-token loop to a
 * handful of attempts.
 */
export const AUTH_PROBE_AFTER_FAILED_HANDSHAKES = 3;

/**
 * Once the server has ACCEPTED a token, don't ask again about that same token for this long. The check is
 * a real request with real side effects (on the V3 API an unpaid account answers HTTP 402, which the
 * shared interceptor turns into a paywall snackbar), so a socket that keeps failing for a reason other
 * than auth must not repeat it on every reconnect. Only a check that got NO response (offline) skips the
 * cooldown, so the first attempt after the network returns asks right away.
 */
export const AUTH_PROBE_COOLDOWN_MS = 10 * 60_000;

export interface WsUrlFactoryDeps {
  /** Current token from storage; null when logged out. Read on EVERY attempt so a rotated token is used. */
  getToken: () => string | null;
  /** HMAC signature for the handshake query string. */
  sign: (token: string) => Promise<string>;
  /** API host without scheme, e.g. `api.refabric.com`. */
  host: string;
  /**
   * One authenticated REST round-trip. On an expired / invalid session the shared HTTP interceptor
   * already clears the stored token and dispatches the app's logout — the SAME path every other 401
   * takes — so this factory re-reads storage afterwards instead of deciding anything itself. A network
   * error may throw: that means "unknown", not "logged out".
   */
  verifySession: () => Promise<unknown>;
  now?: () => number;
}

/**
 * Builds the WebSocket url factory. The server is the authority on the session; the local JWT `exp` and
 * the failed-handshake count are only hints for WHEN to ask it:
 *
 * - token's `exp` already passed by the local clock, or
 * - AUTH_PROBE_AFTER_FAILED_HANDSHAKES handshakes in a row never opened
 *   (the browser cannot show the handshake's HTTP 403, it looks like a network failure).
 *
 * If the probe leaves no token behind, the session is gone and the factory returns null, which stops the
 * reconnect loop — instead of retrying a dead token against the BE every two minutes forever. If the
 * server still accepts the token (a skewed local clock) the connection proceeds normally.
 */
export const createWsUrlFactory = ({ getToken, sign, host, verifySession, now = Date.now }: WsUrlFactoryDeps) => {
  let acceptedToken: string | null = null;
  let acceptedAt = 0;

  return async (ctx?: WsUrlContext): Promise<string | null> => {
    let token = getToken();
    if (!token) return null;

    const failedHandshakes = ctx?.failedHandshakes ?? 0;
    const suspect = isJwtExpired(token, now()) || failedHandshakes >= AUTH_PROBE_AFTER_FAILED_HANDSHAKES;
    const recentlyAccepted = token === acceptedToken && now() - acceptedAt < AUTH_PROBE_COOLDOWN_MS;
    if (suspect && !recentlyAccepted) {
      let answered = false;
      try {
        await verifySession();
        answered = true;
      } catch (error) {
        // A rejected request that still carries an HTTP response (e.g. V3's 402 for an unpaid account) is
        // an answer: cool down. No response at all (offline) is unknown: ask again on the next attempt.
        answered = Boolean((error as { response?: unknown } | null)?.response);
        console.log("[WebSocket] Session check failed, will retry", { status: httpStatusOf(error) });
      }
      const probed = token;
      token = getToken();
      if (!token) {
        console.log("[WebSocket] Session expired — not reconnecting");

        return null;
      }
      if (answered && token === probed) {
        acceptedToken = token;
        acceptedAt = now();
      }
    }

    const signature = await sign(token);

    return `wss://${host}/v1/ws/ws-connect?token=${token}&signature=${signature}`;
  };
};
